import { useState } from 'react';
import scraperApi from 'api';

export type CallbackTestResult = {
  status: 'success' | 'failed';
  message: string;
} | null;

/**
 * Shared hook for crawler form logic (both New and Edit pages)
 */
export const useCrawlerForm = () => {
  const [isTestingCallback, setIsTestingCallback] = useState(false);
  const [callbackTestResult, setCallbackTestResult] = useState<CallbackTestResult>(null);

  /**
   * Test callback URL endpoint
   */
  const testCallback = async (callbackUrl: string) => {
    if (!callbackUrl?.trim()) {
      setCallbackTestResult({
        status: 'failed',
        message: 'Please enter a callback URL',
      });
      return;
    }

    setIsTestingCallback(true);
    setCallbackTestResult(null);

    try {
      const response = await scraperApi.crawlers.testCallback(callbackUrl);

      if (response.success && response.data?.status === 'success') {
        setCallbackTestResult({
          status: 'success',
          message: response.data.message || 'Test successful',
        });
      } else {
        setCallbackTestResult({
          status: 'failed',
          message: response.data?.message || response.message || 'Test failed',
        });
      }
    } catch (error: any) {
      console.error('Callback test error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to test callback URL';
      setCallbackTestResult({
        status: 'failed',
        message: errorMessage,
      });
    } finally {
      setIsTestingCallback(false);
    }
  };

  /**
   * Scroll to first error in form
   */
  const scrollToFirstError = () => {
    const firstErrorField = document.querySelector('.text-red-500');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return {
    isTestingCallback,
    callbackTestResult,
    setCallbackTestResult,
    testCallback,
    scrollToFirstError,
  };
};
